import type {
  GameMode,
  MinecraftItemId,
  Player,
  PlayerPlatform,
  ServerSummary,
  SessionResponse,
  WhitelistEntry,
} from '@crafty-companion/shared';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

let csrfToken: string | null = null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export class ApiClientError extends Error {}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('content-type', 'application/json');
  if (init.method && init.method !== 'GET' && csrfToken) headers.set('x-csrf-token', csrfToken);
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiClientError(payload.error ?? 'Request failed');
  return payload as T;
}

export async function session() {
  const value = await request<SessionResponse>('/api/auth/session');
  csrfToken = value.csrfToken;
  return value;
}

export async function registerOptions(input: {
  displayName: string;
  inviteToken?: string;
  bootstrapToken?: string;
}) {
  return request<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>(
    '/api/auth/register/options',
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export async function registerVerify(challengeId: string, response: unknown) {
  const value = await request<{ verified: boolean; csrfToken: string }>(
    '/api/auth/register/verify',
    { method: 'POST', body: JSON.stringify({ challengeId, response }) },
  );
  csrfToken = value.csrfToken;
  return value;
}

export async function loginOptions(displayName: string) {
  return request<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
    '/api/auth/login/options',
    { method: 'POST', body: JSON.stringify({ displayName }) },
  );
}

export async function loginVerify(challengeId: string, response: unknown) {
  const value = await request<{ verified: boolean; csrfToken: string }>('/api/auth/login/verify', {
    method: 'POST',
    body: JSON.stringify({ challengeId, response }),
  });
  csrfToken = value.csrfToken;
  return value;
}

export const api = {
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  servers: () => request<{ servers: ServerSummary[] }>('/api/servers'),
  serverAction: (id: string, action: 'start' | 'stop') =>
    request(`/api/servers/${id}/${action}`, { method: 'POST' }),
  setMode: (id: string, mode: GameMode) =>
    request(`/api/servers/${id}/mode`, { method: 'PUT', body: JSON.stringify({ mode }) }),
  whitelist: (id: string) => request<{ entries: WhitelistEntry[] }>(`/api/servers/${id}/whitelist`),
  addWhitelist: (id: string, username: string, platform: PlayerPlatform) =>
    request<{ entries: WhitelistEntry[] }>(`/api/servers/${id}/whitelist`, {
      method: 'POST',
      body: JSON.stringify({ username, platform }),
    }),
  removeWhitelist: (id: string, username: string, platform: PlayerPlatform) =>
    request<{ entries: WhitelistEntry[] }>(`/api/servers/${id}/whitelist`, {
      method: 'DELETE',
      body: JSON.stringify({ username, platform }),
    }),
  logs: (id: string, cursor?: number) =>
    request<{ lines: string[]; cursor: number }>(
      `/api/servers/${id}/logs${cursor === undefined ? '' : `?cursor=${cursor}`}`,
    ),
  players: (id: string) => request<{ players: Player[] }>(`/api/servers/${id}/players`),
  teleport: (id: string, source: string, destination: string) =>
    request(`/api/servers/${id}/teleport`, {
      method: 'POST',
      body: JSON.stringify({ source, destination }),
    }),
  giveItem: (id: string, player: string, itemId: MinecraftItemId, amount: number) =>
    request(`/api/servers/${id}/give`, {
      method: 'POST',
      body: JSON.stringify({ player, itemId, amount }),
    }),
  setPlayerMode: (id: string, player: string, mode: GameMode) =>
    request(`/api/servers/${id}/player-mode`, {
      method: 'POST',
      body: JSON.stringify({ player, mode }),
    }),
  killNearbyMobs: (id: string, player: string) =>
    request(`/api/servers/${id}/kill-nearby-mobs`, {
      method: 'POST',
      body: JSON.stringify({ player }),
    }),
  announce: (id: string, text: string) =>
    request(`/api/servers/${id}/announce`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  runCommand: (id: string, command: string) =>
    request(`/api/servers/${id}/command`, {
      method: 'POST',
      body: JSON.stringify({ command }),
    }),
  createInvitation: () =>
    request<{ url: string; expiresAt: number }>('/api/invitations', { method: 'POST' }),
  accounts: () =>
    request<{
      users: Array<{
        id: string;
        displayName: string;
        isOwner: boolean;
        enabled: boolean;
        createdAt: number;
      }>;
    }>('/api/accounts'),
  revokeAccount: (id: string) => request(`/api/accounts/${id}`, { method: 'DELETE' }),
};
